import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { api, pickLang } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ExperiencePlaceholder from "../components/ExperiencePlaceholder";

export default function ExperiencesList() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("__all");

  useEffect(() => {
    api.get("/experiences").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const categories = ["__all", ...Array.from(new Set(items.map((e) => pickLang(e, "category", lang)).filter(Boolean)))];
  const visible = filter === "__all" ? items : items.filter((e) => pickLang(e, "category", lang) === filter);
  const catLabel = (c) => c === "__all" ? t("home.filter_all") : c;

  return (
    <div data-testid="experiences-list">
      <Navbar />
      <section className="pt-32 pb-12 bn-section">
        <div className="bn-container px-6 md:px-8">
          <div className="bn-eyebrow">{t("exp.eyebrow")}</div>
          <h1 className="bn-heading text-4xl md:text-6xl mt-3 max-w-3xl">{t("exp.title")}</h1>

          <div className="mt-10 flex gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className="bn-eyebrow px-4 py-2 transition-all"
                style={{
                  borderRadius: "var(--bn-button-radius)",
                  border: "1px solid var(--bn-border)",
                  background: filter === c ? "var(--bn-primary)" : "transparent",
                  color: filter === c ? "var(--bn-primary-fg)" : "var(--bn-text)",
                }}
                data-testid={`list-filter-${c.toLowerCase()}`}
              >
                {catLabel(c)}
              </button>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {visible.map((exp, idx) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (idx % 6) * 0.05 }}
              >
                <Link to={`/experiencia/${exp.slug || exp.id}`} className="block bn-card overflow-hidden group h-full" data-testid={`list-card-${exp.slug}`}>
                  <div className="relative overflow-hidden aspect-[4/5]">
                    {exp.images?.[0] ? (
                      <img src={exp.images[0]} alt={pickLang(exp, "name", lang)} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                    ) : (
                      <ExperiencePlaceholder />
                    )}
                    <div className="absolute top-4 right-4 px-3 py-1 bn-heading" style={{ background: "var(--bn-bg)", color: "var(--bn-text)", borderRadius: "var(--bn-button-radius)", fontSize: ".95rem" }}>
                      ${exp.price} {exp.currency || "USD"}
                    </div>
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="bn-eyebrow mb-2" style={{ color: "var(--bn-accent)" }}>{pickLang(exp, "category", lang)}</div>
                    <h3 className="bn-heading text-xl md:text-2xl leading-tight">{pickLang(exp, "name", lang)}</h3>
                    <p className="mt-2 text-sm line-clamp-2" style={{ color: "var(--bn-text-muted)" }}>{pickLang(exp, "short_description", lang)}</p>
                    <div className="flex items-center gap-4 mt-4 text-xs" style={{ color: "var(--bn-text-muted)" }}>
                      {pickLang(exp, "duration", lang) && <div className="flex items-center gap-1"><Clock size={12} /> {pickLang(exp, "duration", lang)}</div>}
                      {pickLang(exp, "location", lang) && <div className="flex items-center gap-1"><MapPin size={12} /> {pickLang(exp, "location", lang)}</div>}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
